/**
 * Tab search (⌥⇧A, the strip's ⌄ button) and, on narrow widths, the tab switcher: every open tab and split view in
 * sections — pinned, each group (with its colour), then the rest — filtered with the shared Search.rank (cmdk).
 */
import { Search, Shell, type ShellState, type ShellViewModel, type TabUnit } from "@bundle/core";
import { Icon, useT } from "@bundle/ui";
import { Command } from "cmdk";
import { Fragment, useMemo, useState } from "react";
import type { TabInfo } from "../tabInfo.ts";
import { groupLabel, TabIcon } from "./parts.tsx";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: ShellState;
  shell: ShellViewModel;
  info: (tabId: string) => TabInfo;
  /** Narrow widths: a full-height sheet that lists everything (the tab switcher). */
  switcher: boolean;
};

type Section = { id: string; heading: string; color?: string; units: TabUnit[] };

export function TabSearch(p: Props) {
  const t = useT();
  const [query, setQuery] = useState("");
  const { state, shell, info } = p;

  const sections = useMemo<Section[]>(() => {
    const units = Shell.tabUnits(state);
    const text = (u: TabUnit) => u.tabIds.map((id) => info(id).title).join(" | ");
    let keep: Set<string> | undefined;
    if (query.trim() !== "") {
      const ranked = Search.rank(query, units.map((u) => ({ id: u.id, text: text(u) })), 100);
      keep = new Set(ranked);
    }
    const shown = (u: TabUnit) => keep === undefined || keep.has(u.id);
    const out: Section[] = [];
    const pinned = units.filter((u) => u.pinned && shown(u));
    if (pinned.length > 0) out.push({ id: "pinned", heading: t("shell.tabs.pinned"), units: pinned });
    for (const g of state.groups) {
      const us = units.filter((u) => u.groupId === g.id && shown(u));
      if (us.length > 0) out.push({ id: `group:${g.id}`, heading: groupLabel(g, t), color: g.color, units: us });
    }
    const rest = units.filter((u) => !u.pinned && u.groupId === undefined && shown(u));
    if (rest.length > 0) out.push({ id: "tabs", heading: t("shell.tabs.ungrouped"), units: rest });
    return out;
  }, [state, info, query, t]);

  const close = (o: boolean) => {
    p.onOpenChange(o);
    if (!o) setQuery("");
  };

  return (
    <Command.Dialog
      open={p.open}
      onOpenChange={close}
      label={t("shell.tabs.search")}
      shouldFilter={false}
      loop
      overlayClassName="omnibox-overlay"
      contentClassName={p.switcher ? "omnibox tab-search tab-search-switcher" : "omnibox tab-search"}
    >
      <div className="omnibox-input">
        <Icon name="search" size="icon.md" color="text.tertiary" />
        <Command.Input value={query} onValueChange={setQuery} placeholder={t("shell.tabs.searchPlaceholder")} data-testid="tab-search-input" />
        <kbd>esc</kbd>
      </div>
      <Command.List className="omnibox-list tab-search-list">
        <Command.Empty className="omnibox-empty">{t("shell.omnibox.noResults")}</Command.Empty>
        {sections.map((sec) => (
          <Command.Group
            key={sec.id}
            className="omnibox-group"
            heading={
              <span className="tab-search-heading">
                {sec.color === undefined ? null : <span className="tab-group-dot" data-group-color={sec.color} />}
                <span>{sec.heading}</span>
              </span>
            }
          >
            {sec.units.map((u) => (
              <Command.Item
                key={u.id}
                value={u.id}
                className={["omnibox-item", u.shown && "tab-search-current"].filter(Boolean).join(" ")}
                onSelect={() => {
                  close(false);
                  shell.activateTab(u.tabIds[0]!);
                }}
                data-testid={`tab-search-${u.id}`}
              >
                <span className="tab-search-unit">
                  {u.tabIds.map((id, i) => {
                    const inf = info(id);
                    return (
                      <Fragment key={id}>
                        {i > 0 ? <span className="tab-split-divider" aria-hidden /> : null}
                        <span className="tab-search-seg">
                          <TabIcon info={inf} />
                          <span className="omnibox-item-label">{inf.title}</span>
                        </span>
                      </Fragment>
                    );
                  })}
                </span>
                {u.pinned ? <Icon name="pin" size="icon.sm" color="text.tertiary" /> : null}
                <button
                  type="button"
                  className="tab-close tab-search-close"
                  aria-label={u.split ? t("shell.split.close") : t("shell.closeTab")}
                  data-testid={`tab-search-close-${u.id}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (u.split) shell.closeSplit(u.tabIds[0]!);
                    else shell.closeTab(u.tabIds[0]!);
                  }}
                >
                  <Icon name="close" size="icon.sm" />
                </button>
              </Command.Item>
            ))}
          </Command.Group>
        ))}
      </Command.List>
      <div className="omnibox-footer">{t("shell.tabs.count", { count: state.tabs.length })}</div>
    </Command.Dialog>
  );
}
